/**
 * FriendsTab Component
 * Manages friend requests (incoming/outgoing) and displays the friends list.
 * Sections: Pending Requests, My Friends, Outgoing Requests (collapsible).
 */

import LoadingSpinner from '@components/ui/LoadingSpinner';
import { API_BASE_URL } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useFriendRequests } from '@hooks/useFriendRequests';
import { useFriends } from '@hooks/useFriends';
import { ApiError, createHttpClient } from '@parlay-picker/shared/api/httpClient';
import { formatTimeAgo } from '@parlay-picker/shared/formatting';
import type {
  FriendInfo,
  IncomingFriendRequest,
  OutgoingFriendRequest,
} from '@parlay-picker/shared/friendTypes';
import {
  Check,
  ChevronDown,
  Clock,
  Loader2,
  User,
  UserMinus,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

// =============================================================================
// Sub-components
// =============================================================================

interface IncomingRequestCardProps {
  request: IncomingFriendRequest;
  onAccept: (requestId: number) => Promise<void>;
  onReject: (requestId: number) => Promise<void>;
  loadingId: number | null;
}

function IncomingRequestCard({
  request,
  onAccept,
  onReject,
  loadingId,
}: IncomingRequestCardProps): ReactElement {
  const isLoading = loadingId === request.id;

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4 border border-theme-border transition-all hover:bg-theme-surface">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Avatar placeholder */}
          <div className="w-10 h-10 rounded-full bg-theme-surface-hover flex items-center justify-center">
            <User className="w-5 h-5 text-theme-text-secondary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-200">
                {request.sender.displayName}
              </span>
              {request.sender.isVerified && (
                <span className="text-xs px-1.5 py-0.5 bg-theme-primary/20 text-theme-primary rounded font-medium">
                  Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-theme-text-tertiary mt-0.5">
              <Clock className="w-3 h-3" />
              <span>{formatTimeAgo(request.createdAt)}</span>
              {request.sender.followerCount > 0 && (
                <>
                  <span className="mx-1">--</span>
                  <span>{request.sender.followerCount} followers</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onReject(request.id)}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-theme-border text-theme-text-secondary hover:bg-theme-surface-hover hover:text-theme-text transition-colors disabled:opacity-50 min-h-8"
          >
            <X className="w-3.5 h-3.5" />
            Decline
          </button>
          <button
            type="button"
            onClick={() => onAccept(request.id)}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-theme-primary-fill text-theme-on-primary-fill hover:bg-theme-primary-fill-hover transition-colors disabled:opacity-50 min-h-8"
          >
            {isLoading ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

interface OutgoingRequestCardProps {
  request: OutgoingFriendRequest;
  onCancel: (requestId: number) => Promise<void>;
  loadingId: number | null;
}

function OutgoingRequestCard({
  request,
  onCancel,
  loadingId,
}: OutgoingRequestCardProps): ReactElement {
  const isLoading = loadingId === request.id;

  return (
    <div className="bg-theme-surface/50 rounded-lg p-3 border border-theme-border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-theme-surface-hover flex items-center justify-center">
            <User className="w-4 h-4 text-theme-text-secondary" />
          </div>
          <div>
            <span className="text-sm font-medium text-gray-200">
              {request.receiver.displayName}
            </span>
            <div className="flex items-center gap-1 text-xs text-theme-text-tertiary mt-0.5">
              <Clock className="w-3 h-3" />
              <span>Sent {formatTimeAgo(request.createdAt)}</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onCancel(request.id)}
          disabled={isLoading}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-theme-border text-theme-text-tertiary hover:text-theme-danger hover:border-theme-danger/30 transition-colors disabled:opacity-50 min-h-8"
        >
          {isLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <X className="w-3.5 h-3.5" />
          )}
          Cancel
        </button>
      </div>
    </div>
  );
}

interface FriendCardProps {
  friend: FriendInfo;
  onUnfriend: (friendId: string) => void;
}

function FriendCard({ friend, onUnfriend }: FriendCardProps): ReactElement {
  return (
    <div className="bg-theme-surface/50 rounded-lg p-4 border border-theme-border transition-all hover:bg-theme-surface">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-theme-surface-hover flex items-center justify-center">
            <User className="w-5 h-5 text-theme-text-secondary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-200">{friend.displayName}</span>
              {friend.isVerified && (
                <span className="text-xs px-1.5 py-0.5 bg-theme-primary/20 text-theme-primary rounded font-medium">
                  Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-theme-text-tertiary mt-0.5">
              <span>
                Friends since{' '}
                {new Date(friend.friendSince).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              {friend.followerCount > 0 && (
                <>
                  <span className="mx-1">--</span>
                  <span>{friend.followerCount} followers</span>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onUnfriend(friend.id)}
          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-theme-border text-theme-text-tertiary hover:text-theme-danger hover:border-theme-danger/30 transition-colors min-h-8"
        >
          <UserMinus className="w-3.5 h-3.5" />
          Unfriend
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Unfriend Confirmation Dialog
// =============================================================================

interface UnfriendDialogProps {
  friendName: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

function UnfriendDialog({
  friendName,
  onConfirm,
  onCancel,
  loading,
}: UnfriendDialogProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-theme-bg border border-theme-border rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-100 mb-2">Unfriend {friendName}?</h3>
        <p className="text-sm text-theme-text-secondary mb-6">
          You will no longer be friends. You can send a new friend request later.
        </p>
        <div className="flex items-center gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-theme-border text-theme-text-secondary hover:bg-theme-surface-hover transition-colors disabled:opacity-50 min-h-10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-theme-danger text-white hover:bg-red-600 transition-colors disabled:opacity-50 min-h-10"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Unfriend
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function FriendsTab(): ReactElement {
  const { authFetch } = useAuth();
  const { showToast } = useToast();

  const httpClient = useMemo(
    () => createHttpClient({ authFetch, baseUrl: API_BASE_URL }),
    [authFetch],
  );

  const {
    incoming,
    outgoing,
    incomingTotal,
    outgoingTotal,
    incomingHasMore,
    outgoingHasMore,
    loading: requestsLoading,
    error: requestsError,
    reload: reloadRequests,
    loadMoreIncoming,
    loadMoreOutgoing,
  } = useFriendRequests();

  const {
    friends,
    total: friendsTotal,
    hasMore: friendsHasMore,
    loading: friendsLoading,
    error: friendsError,
    loadFriends,
    loadMore: loadMoreFriends,
  } = useFriends();

  const [hasFetched, setHasFetched] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [unfriendTarget, setUnfriendTarget] = useState<{ id: string; name: string } | null>(null);
  const [unfriendLoading, setUnfriendLoading] = useState(false);
  const [outgoingExpanded, setOutgoingExpanded] = useState(false);

  // Load data on mount
  useEffect(() => {
    if (!hasFetched) {
      Promise.all([reloadRequests(), loadFriends()]).then(() => setHasFetched(true));
    }
  }, [hasFetched, reloadRequests, loadFriends]);

  // Accept incoming friend request
  const handleAcceptRequest = useCallback(
    async (requestId: number) => {
      setActionLoadingId(requestId);
      try {
        await httpClient.post(`/api/friends/request/${requestId}/accept`);
        await Promise.all([reloadRequests(), loadFriends()]);
        showToast('Friend request accepted!', 'success');
      } catch (err) {
        showToast(ApiError.getUserMessage(err, 'Failed to accept request'), 'error');
      } finally {
        setActionLoadingId(null);
      }
    },
    [httpClient, reloadRequests, loadFriends, showToast],
  );

  // Reject incoming friend request
  const handleRejectRequest = useCallback(
    async (requestId: number) => {
      setActionLoadingId(requestId);
      try {
        await httpClient.post(`/api/friends/request/${requestId}/reject`);
        await reloadRequests();
        showToast('Friend request declined', 'info');
      } catch (err) {
        showToast(ApiError.getUserMessage(err, 'Failed to decline request'), 'error');
      } finally {
        setActionLoadingId(null);
      }
    },
    [httpClient, reloadRequests, showToast],
  );

  // Cancel outgoing friend request
  const handleCancelRequest = useCallback(
    async (requestId: number) => {
      setActionLoadingId(requestId);
      try {
        await httpClient.delete(`/api/friends/request/${requestId}`);
        await reloadRequests();
        showToast('Friend request cancelled', 'info');
      } catch (err) {
        showToast(ApiError.getUserMessage(err, 'Failed to cancel request'), 'error');
      } finally {
        setActionLoadingId(null);
      }
    },
    [httpClient, reloadRequests, showToast],
  );

  // Unfriend (with confirmation dialog)
  const handleUnfriendConfirm = useCallback(async () => {
    if (!unfriendTarget) return;
    setUnfriendLoading(true);
    try {
      await httpClient.delete(`/api/friends/${unfriendTarget.id}`);
      await loadFriends();
      showToast(`Unfriended ${unfriendTarget.name}`, 'info');
      setUnfriendTarget(null);
    } catch (err) {
      showToast(ApiError.getUserMessage(err, 'Failed to unfriend user'), 'error');
    } finally {
      setUnfriendLoading(false);
    }
  }, [httpClient, unfriendTarget, loadFriends, showToast]);

  // Initial loading state
  if (!hasFetched && (requestsLoading || friendsLoading)) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  // Error state
  if (requestsError || friendsError) {
    return (
      <div className="bg-theme-danger/10 border border-theme-danger/30 rounded-lg p-4 text-theme-danger">
        {requestsError || friendsError}
      </div>
    );
  }

  const hasIncoming = incoming.length > 0;
  const hasOutgoing = outgoing.length > 0;
  const hasFriends = friends.length > 0;
  const isEmpty = !hasIncoming && !hasOutgoing && !hasFriends;

  return (
    <div className="space-y-6">
      {/* Empty state */}
      {isEmpty && (
        <div className="rebrand-empty-state p-8 sm:p-12 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 rounded-2xl bg-theme-primary/10 flex items-center justify-center mx-auto mb-5">
            <Users className="w-8 h-8 text-theme-primary" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No friends yet</h3>
          <p className="text-theme-text-tertiary">
            Send friend requests from user profiles to start building your network.
          </p>
        </div>
      )}

      {/* Pending Incoming Requests */}
      {hasIncoming && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="w-4 h-4 text-theme-primary" />
            <h3 className="text-sm font-semibold text-gray-200">Pending Requests</h3>
            <span className="px-1.5 py-0.5 text-xs font-bold bg-theme-primary-fill text-theme-on-primary-fill rounded-full min-w-[18px] text-center">
              {incomingTotal}
            </span>
          </div>
          <div className="space-y-2">
            {incoming.map((request) => (
              <IncomingRequestCard
                key={request.id}
                request={request}
                onAccept={handleAcceptRequest}
                onReject={handleRejectRequest}
                loadingId={actionLoadingId}
              />
            ))}
          </div>
          {incomingHasMore && (
            <div className="flex justify-center pt-3">
              <button
                type="button"
                onClick={loadMoreIncoming}
                disabled={requestsLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-theme-primary hover:text-theme-primary-hover bg-theme-surface border border-theme-border rounded-lg hover:bg-theme-surface-hover transition-colors disabled:opacity-50 min-h-10"
              >
                {requestsLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* My Friends */}
      {hasFriends && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-theme-text-secondary" />
            <h3 className="text-sm font-semibold text-gray-200">My Friends</h3>
            <span className="text-xs text-theme-text-tertiary">({friendsTotal})</span>
          </div>
          <div className="space-y-2">
            {friends.map((friend) => (
              <FriendCard
                key={friend.id}
                friend={friend}
                onUnfriend={(id) => setUnfriendTarget({ id, name: friend.displayName })}
              />
            ))}
          </div>
          {friendsHasMore && (
            <div className="flex justify-center pt-3">
              <button
                type="button"
                onClick={loadMoreFriends}
                disabled={friendsLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-theme-primary hover:text-theme-primary-hover bg-theme-surface border border-theme-border rounded-lg hover:bg-theme-surface-hover transition-colors disabled:opacity-50 min-h-10"
              >
                {friendsLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Outgoing Requests (collapsible) */}
      {hasOutgoing && (
        <div>
          <button
            type="button"
            onClick={() => setOutgoingExpanded(!outgoingExpanded)}
            className="flex items-center gap-2 mb-3 group"
          >
            <Clock className="w-4 h-4 text-theme-text-tertiary" />
            <h3 className="text-sm font-semibold text-theme-text-secondary group-hover:text-gray-200 transition-colors">
              Sent Requests
            </h3>
            <span className="text-xs text-theme-text-tertiary">({outgoingTotal})</span>
            <ChevronDown
              className={`w-4 h-4 text-theme-text-tertiary transition-transform ${
                outgoingExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
          {outgoingExpanded && (
            <>
              <div className="space-y-2">
                {outgoing.map((request) => (
                  <OutgoingRequestCard
                    key={request.id}
                    request={request}
                    onCancel={handleCancelRequest}
                    loadingId={actionLoadingId}
                  />
                ))}
              </div>
              {outgoingHasMore && (
                <div className="flex justify-center pt-3">
                  <button
                    type="button"
                    onClick={loadMoreOutgoing}
                    disabled={requestsLoading}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-theme-primary hover:text-theme-primary-hover bg-theme-surface border border-theme-border rounded-lg hover:bg-theme-surface-hover transition-colors disabled:opacity-50 min-h-10"
                  >
                    {requestsLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Unfriend Confirmation Dialog */}
      {unfriendTarget && (
        <UnfriendDialog
          friendName={unfriendTarget.name}
          onConfirm={handleUnfriendConfirm}
          onCancel={() => setUnfriendTarget(null)}
          loading={unfriendLoading}
        />
      )}
    </div>
  );
}
